import { defineStore } from 'pinia'
import { registrationApi } from '@modules/auth/services/api/registrationApi'

interface State {
  privacyPolicyUrl: string
  termsAndConditionsUrl: string
  loaded: boolean
  loading: boolean
  loadingPromise: Promise<void> | null
}

/**
 * Source of truth for the Privacy Policy / Terms & Conditions URLs.
 *
 * URLs are fetched once from `GET /api/registration/terms` and cached for the
 * session. `loadLegalLinks()` is safe to call from multiple places — concurrent
 * calls share the same in-flight request, and subsequent calls short-circuit
 * once the response has been cached.
 *
 * The endpoint is unauthenticated (only requires the global `x-api-key`
 * header), so it can be called from pre-login flows (registration, login,
 * forgot-password) as well.
 */
export const useLegalLinksStore = defineStore('legalLinks', {
  state: (): State => ({
    privacyPolicyUrl: '',
    termsAndConditionsUrl: '',
    loaded: false,
    loading: false,
    loadingPromise: null,
  }),

  actions: {
    async loadLegalLinks(force = false): Promise<void> {
      if (this.loaded && !force) return
      if (this.loadingPromise) return this.loadingPromise

      this.loading = true
      this.loadingPromise = (async () => {
        try {
          const { data } = await registrationApi.getTerms()
          this.privacyPolicyUrl = data?.privacyPolicyUrl || ''
          this.termsAndConditionsUrl = data?.termsAndConditionsUrl || ''
          this.loaded = true
        } catch (e) {
          console.warn('[legalLinks] failed to load terms', e)
        } finally {
          this.loading = false
          this.loadingPromise = null
        }
      })()

      return this.loadingPromise
    },
  },
})
